// React imports
import React, { useEffect, useState, useRef } from "react";

// External library imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Chip,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArticleIcon from "@mui/icons-material/Article";
import RateReviewIcon from "@mui/icons-material/RateReview";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import dayjs from "dayjs";

// Local imports
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import {
  ViewUser,
  Employee_and_manager,
  subordinateDetails,
  changePassword,
  getReportingManager,
  getDirectReports,
} from "../../Service/Apis";
import useAxiosInterceptor from "../../components/layout/AxiosInstance";
import EmployeeSkillSet from "../Skill Set/EmployeeSkillSet";
import capitalizeFirstLetter from "../../components/functions/NameFunc";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import { RootState } from "../../store/Store";
import {
  DataPayload,
  FetchDataFailureAction,
  FetchDataSuccessAction,
  patchData,
  fetchData,
} from "../../store/actions";
import { postDataSuccess } from "../../store/AxiosSlice";
import { EmployeeData, ManagerData, SubordinateData, PasswordData, GlobalManager, DirectReport } from "./Types/LandingPageTypes";

const LandingPage: React.FC = () => {
  // 1. useState declarations
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [managerData, setManagerData] = useState<ManagerData | null>(null);
  const [subordinateData, setSubordinateData] = useState<
    SubordinateData[] | null
  >(null);
  const [globalManager, setGlobalManager] = useState<GlobalManager>({ id: null, name: null });
  const [globalDirectReports, setGlobalDirectReports] = useState<DirectReport[]>([]);
  const [open, setOpen] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordFocused1, setPasswordFocused1] = useState(false);
  const [passwordFocused2, setPasswordFocused2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  // 2. const variables (hooks, Redux, computed constants)
  const email = localStorage.getItem("email");
  const token = localStorage.getItem("token");
  const axiosInstance = useAxiosInterceptor();
  const dispatch = useDispatch();
  const showToast = useCustomToast();
  const oldPasswordFieldRef = useRef<HTMLInputElement | null>(null);
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  const roles = useSelector((state: RootState) => state.auth.roles || []);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch Employee Data
        const employeeResponse = await dispatch2(
          fetchData(`${ViewUser}${email}`)
        );
        setEmployeeData(employeeResponse[0]);

        const empId = employeeResponse[0]?.id;
        if (empId) {
          // Global manager for current user
          try {
            const rm = await dispatch2(fetchData(`${getReportingManager}?employee_id=${empId}`));
            setGlobalManager({ id: rm?.reporting_manager_id ?? null, name: rm?.reporting_manager_name ?? null });
          } catch (e) {
            setGlobalManager({ id: null, name: null });
          }

          // Global direct reports using actual assigned roles (not selectedRole)
          try {
            const hasManagerRole = (roles || []).some((r: string) => (r || "").toLowerCase() === "manager");
            const isPrivileged = (roles || []).some((r: string) => ["admin", "hr"].includes((r || "").toLowerCase()));

            let list: Array<{ id: string; first_name: string; last_name: string }> = [];

            if (hasManagerRole) {
              const dr = await dispatch2(fetchData(`${getDirectReports}`));
              list = dr?.direct_reports || [];
            } else if (isPrivileged && empId) {
              const dr = await dispatch2(fetchData(`${getDirectReports}?manager_id=${empId}`));
              list = dr?.direct_reports || [];
            } else {
              list = [];
            }

            setGlobalDirectReports(list || []);
          } catch (e) {
            // If forbidden or other error, keep empty and avoid further calls
            setGlobalDirectReports([]);
          }
        }

        // Fetch Manager Data
        const managerResponse = await dispatch2(
          fetchData(Employee_and_manager)
        );

        if (managerResponse.managers && managerResponse.managers.length > 0) {
          // Group projects by manager name
          const managerProjectMap: Record<string, string[]> = {};
          managerResponse.managers.forEach((mgr: { reporting_manager_name?: string; project_name?: string }) => {
            const name = mgr.reporting_manager_name || "No Manager Assigned";
            const project = mgr.project_name || "No Project Assigned";
            if (!managerProjectMap[name]) {
              managerProjectMap[name] = [];
            }
            managerProjectMap[name].push(project);
          });

          // Save as array of {name, projects}
          const managersWithProjects = Object.entries(managerProjectMap).map(
            ([name, projects]) => ({
              name,
              projects,
            })
          );

          setManagerData({
            reporting_managers: managersWithProjects,
            project_names: [], 
          });
        } else {
          setManagerData({
            reporting_managers: [],
            project_names: [],
          });
        }

        // Fetch Subordinate Data
        const subordinateResponse = await dispatch2(
          fetchData(subordinateDetails)
        );
        setSubordinateData(subordinateResponse);
      } catch (error: unknown) {
        // Error fetching data - silently fail to avoid disrupting user experience
      }
    };

    // Only run when we have an email (logged in) and roles loaded (non-empty or at least defined)
    fetchAllData();
  }, [email, roles]);

  const handleOpen = () => {
    setOpen(true);
    oldPasswordFieldRef.current?.focus();
  };

  const handleClose = (): void => {
    setOpen(false);
    setPasswordData({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitPasswordChange = (e: React.FormEvent): void => {
    e.preventDefault();
    try {
      const response = dispatch2(
        patchData(`${changePassword}${employeeData?.id}/`, passwordData)
      )
        .then((response) => {
          showToast("Password Changed Successfully", "success", true);
          dispatch(postDataSuccess(null));
          handleClose();
        })
        .catch((error: unknown) => {
          const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
          const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to Change Password";
          showToast(errorMessage, "error", true);
        });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to Change Password";
      showToast(errorMessage, "error", true);
    }
  };

  const handlePasswordFocus = (): void => {
    setPasswordFocused(true);
  };

  const handlePasswordBlur = (): void => {
    setPasswordFocused(false);
  };

  const handlePasswordFocus1 = (): void => {
    setPasswordFocused1(true);
  };

  const handlePasswordBlur1 = (): void => {
    setPasswordFocused1(false);
  };

  const handlePasswordFocus2 = (): void => {
    setPasswordFocused2(true);
  };

  const handlePasswordBlur2 = (): void => {
    setPasswordFocused2(false);
  };

  const handleTogglePassword = (): void => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleTogglePassword1 = (): void => {
    setShowPassword1((prevShowPassword) => !prevShowPassword);
  };

  const handleTogglePassword2 = (): void => {
    setShowPassword2((prevShowPassword) => !prevShowPassword);
  };

  // Quick links data
  const quickLinks = [
    {
      id: 1,
      title: "My Reviews",
      path: "/pms/my-reviews",
      description: "Track your performance reviews",
      icon: <RateReviewIcon />,
    },
    {
      id: 2,
      title: "Utilization",
      path: "/utilization",
      description: "Analyze time spent on tasks",
      icon: <AccessTimeIcon />,
    },
    {
      id: 3,
      title: "Update Skills",
      path: "/employee-skill-set",
      description: "Add or modify your skills",
      icon: <LeaderboardIcon />,
    },
    {
      id: 4,
      title: "Past Appraisals",
      path: "/historical-appraisal",
      description: "Graph record of past appraisals",
      icon: <AssessmentIcon />,
    },
    // COMMENTED OUT - Previous Responses card hidden
    // {
    //   id: 5,
    //   title: "Previous Responses",
    //   path: "/previousresponses",
    //   description: "View past responses and evaluations",
    //   icon: <ArticleIcon />,
    // },
  ];
  const uniqueManagers =
    managerData && managerData.reporting_managers
      ? Array.from(new Set(managerData.reporting_managers))
      : [];
  return (
    <>
      <Navbar />
      <Box sx={{ mt: 5, mb: 10, backgroundColor: "#f5f7fa", p: 4 }}>
        {/* Top Row - Personal Details and Reporting Structure Cards */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Personal Details Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ boxShadow: 3, borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#007300" }}
                >
                  Personal Details
                </Typography>
                {employeeData ? (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Name:</strong> {employeeData.first_name}{" "}
                          {employeeData.last_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Email:</strong> {employeeData.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Gender:</strong> {employeeData.gender}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Date of Birth:</strong>{" "}
                          {new Date(
                            employeeData.date_of_birth
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Role:</strong> {employeeData.roles.join(", ")}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Designation:</strong>{" "}
                          {employeeData.designation}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Date of Joining:</strong>{" "}
                          {new Date(
                            employeeData.date_of_joining
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Employee Code:</strong>{" "}
                          {employeeData.employee_code}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Button
                      variant="contained"
                      onClick={handleOpen}
                      sx={{
                        backgroundColor: "#007300",
                        "&:hover": {
                          backgroundColor: "#005500",
                        },
                      }}
                    >
                      Change Password
                    </Button>
                  </>
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Reporting Structure Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ boxShadow: 3, borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#007300" }}
                >
                  Reporting Structure
                </Typography>

                {managerData ? (
                  <>
                    <Typography sx={{ mb: 1 }} fontWeight="bold">
                      Global Reporting Manager: {globalManager?.name || "N/A"}
                    </Typography>
                    {/* COMMENTED OUT - Reports To section hidden */}
                    {/* <Typography sx={{ mb: 2 }} fontWeight="bold">
                      Reports To:{" "}
                      {managerData && managerData.reporting_managers.length > 0
                        ? managerData.reporting_managers
                            .map(
                              (mgr) =>
                                `${mgr.name} (${mgr.projects.join(", ")})`
                            )
                            .join(", ")
                        : "N/A"}
                    </Typography> */}
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Direct Reports (Global):
                    </Typography>
                    {globalDirectReports.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}
                      >
                        {globalDirectReports.map((r) => (
                          <Chip
                            key={r.id}
                            label={`${r.first_name} ${r.last_name}`}
                            sx={{ color: "#007300" }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ mb: 2 }}>N/A</Typography>
                    )}
                    {/* COMMENTED OUT - Direct Reports (By Team/Project) section hidden */}
                    {/* <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Direct Reports (By Team/Project):
                    </Typography>
                    {subordinateData && subordinateData.length > 0 ? (
                      subordinateData.map((team) => (
                        <Box key={team.team_id} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Project: {team.project_name}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: "wrap", gap: 1 }}
                          >
                            {team.reporting_team_members.map((member) => (
                              <Chip
                                key={member.team_member_id}
                                label={`${member.employee_detail.first_name} ${member.employee_detail.last_name}`}
                                sx={{
                                  color: "#007300",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      ))
                    ) : (
                      <Typography>N/A</Typography>
                    )} */}
                  </>
                ) : (
                  <Typography>Loading Manager Data...</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Links Section */}
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#007300", mb: 3 }}
            >
              Quick Links
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ flexWrap: "wrap", gap: 2, justifyContent: "center" }}
            >
              {quickLinks.map((link) => (
                <Button
                  key={link.id}
                  variant="contained"
                  href={link.path}
                  sx={{
                    backgroundColor: "#ebebeb",
                    color: "#007300",
                    "&:hover": {
                      backgroundColor: "#007300",
                      color: "#fff",
                    },
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 2,
                    width: "100%",
                    maxWidth: "300px",
                  }}
                >
                  <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                    {link.icon}
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {link.title}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {link.description}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Footer />

      {/* Change Password Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableEnforceFocus
      >
        <Box component="form" onSubmit={handleSubmitPasswordChange}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <IconButton onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography fontWeight={"bold"} variant="h5">
              Change Password
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  m: 2,
                }}
              >
                <Typography fontWeight={"bold"} sx={{ width: "14rem" }}>
                  Old Password :
                </Typography>
                <TextField
                  id="old_password"
                  autoFocus
                  inputRef={oldPasswordFieldRef}
                  onFocus={handlePasswordFocus}
                  onBlur={handlePasswordBlur}
                  type={showPassword ? "text" : "password"}
                  name="old_password"
                  variant="standard"
                  size="small"
                  required
                  value={passwordData.old_password}
                  InputProps={{
                    inputProps: { tabIndex: 0 },
                    endAdornment: (
                      <InputAdornment position="end">
                        {passwordFocused && (
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: "16rem" }}
                  onChange={handlePasswordChange}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  m: 2,
                }}
              >
                <Typography fontWeight={"bold"} sx={{ width: "14rem" }}>
                  New Password :
                </Typography>

                <TextField
                  required
                  id="new_password"
                  type={showPassword1 ? "text" : "password"}
                  onFocus={handlePasswordFocus1}
                  onBlur={handlePasswordBlur1}
                  name="new_password"
                  variant="standard"
                  size="small"
                  value={passwordData.new_password}
                  sx={{ width: "16rem" }}
                  onChange={handlePasswordChange}
                  helperText="Must be 8 characters and should have at least one special character"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {passwordFocused1 && (
                          <IconButton
                            onClick={handleTogglePassword1}
                            edge="end"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {showPassword1 ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  m: 2,
                }}
              >
                <Typography fontWeight={"bold"} sx={{ width: "14rem" }}>
                  Confirm Password :
                </Typography>
                <Tooltip title="Re-enter your password">
                  <TextField
                    required
                    id="confirm_password"
                    type={showPassword2 ? "text" : "password"}
                    name="confirm_password"
                    size="small"
                    onFocus={handlePasswordFocus2}
                    onBlur={handlePasswordBlur2}
                    variant="standard"
                    value={passwordData.confirm_password}
                    sx={{ width: "16rem" }}
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {passwordFocused2 && (
                            <IconButton
                              onClick={handleTogglePassword2}
                              edge="end"
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {showPassword2 ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ m: 2, p: 1 }}>
            <Button onClick={handleClose}>Close</Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "#007300",
                "&:hover": {
                  backgroundColor: "#005500",
                },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default LandingPage;
